import { MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type { ProductType } from "../models/ProductType";

export const getProductTypes = async ({
	id = null,
}: {
	id?: number | null;
} = {}): Promise<ProductType[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "OnlyActives", value: false }];

	if (id) {
		parameters.push({ name: "Id", value: id });
	}

	const resp = await apiService.callV2("[MILL].[GetProductTypes]", parameters);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as ProductType[];
	}
	throw new Error(resp.message || "Error fetching product types");
};

export interface UpsertProductTypeResult {
	// true = not applied; deactivating a type that still has active product sizes.
	requiresConfirmation: boolean;
	activeDependents?: number;
	productType?: ProductType;
}

export const upsertProductType = async (
	productType: ProductType,
	force = false,
): Promise<UpsertProductTypeResult> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "Id", value: productType.Id },
		{ name: "Name", value: productType.Name },
		{ name: "Description", value: productType.Description },
		{ name: "TPHGoal", value: productType.TPHGoal },
		{ name: "Prefix", value: productType.Prefix },
		{ name: "Active", value: productType.Active },
		{ name: "Force", value: force },
	];

	const resp = await apiService.callV2(
		"[MILL].[UpsertProductType]",
		parameters,
	);

	if (resp.ok) {
		const row = get(resp, "data.tables[0].rows[0]", null) as
			| (ProductType & { RequiresConfirmation?: number; ActiveDependents?: number })
			| null;
		if (row?.RequiresConfirmation) {
			return {
				requiresConfirmation: true,
				activeDependents: row.ActiveDependents,
			};
		}
		return { requiresConfirmation: false, productType: row as ProductType };
	}
	throw new Error(resp.message || "Error saving product type");
};

export const removeProductType = async (id: number): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "Id", value: id }];

	const resp = await apiService.callV2(
		"[MILL].[RemoveProductType]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting product type");
	}
};
